import {
  SVG,
  TierInfo,
  UserTierTypes,
  formatNumber,
  useIsDesktop,
  useIsLG,
  useIsWeb,
  ProgressBar,
} from '@eCommerce/frontend-legacy-shared-library-native';
import BottomRightWaves from 'assets/wavesBottomRight.svg';
import {Text, View} from 'native-base';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Platform} from 'react-native';
import {AnimatedCircularProgress} from 'react-native-circular-progress';

import Divider from './Divider';
import NextTierCredits from './NextTierCredits/NextTierCredits';
import UserInfoSummary from './UserInfoSummary';
import {useTheme} from 'styled-components/native';
import SEVEN_STARS_PATTERN from 'assets/sevenstars_pattern.svg';
import SEVEN_STARS_TEXT_LOGO from 'assets/seven_stars_text_logo.svg';
import CurrentTierCredits from './CurrentTierCredits';
import SevenStarsInnerCircle, {
  getCreditsToNextExperienceCredit,
  getSevenStarsExperienceProgress,
} from './SevenStarsInnerCircle/SevenStarsInnerCircle';
import InnerCircle from './InnerCircle';
import {getTierProgress} from 'utils/tierProgress';

export interface RewardsSummaryModuleProps {
  rewardCredits: number;
  tierCredits: number;
  currentLevel?: UserTierTypes; // Optional to handle loading state before API returns data
  currentTierInfo?: TierInfo; // Optional to handle loading state before API returns data
  nextTierInfo?: TierInfo; // Optional to handle loading state before API returns data
  allTiers?: TierInfo[];
  isGiftedTier?: boolean;
  fullName: string;
  accountId: string;
  asOfDate: string;
  expirationDate: string;
  lastEarnedDate: string;
}

const RewardsSummaryModule: React.FC<RewardsSummaryModuleProps> = props => {
  const {
    rewardCredits,
    tierCredits,
    currentLevel,
    currentTierInfo,
    nextTierInfo,
    allTiers,
    fullName,
    accountId,
    isGiftedTier = false,
    asOfDate,
    expirationDate,
    lastEarnedDate,
  } = props;

  const [isOpen, setIsOpen] = useState(true);

  const isLG = useIsLG();
  const isDesktop = useIsDesktop();
  const isWeb = useIsWeb();
  const theme = useTheme() as any;
  const {
    paleSky,
    shuttleGray,
    athens,
    white,
    addAlpha,
    richBlack,
    tuna,
    tierColors,
  } = theme.colors;

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Fade in/out background waves when user expands/collapses the module
  const fadeWavesIn = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  };

  const fadeWavesOut = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  };

  useEffect(() => {
    if (isOpen) {
      fadeWavesIn();
    }

    if (!isOpen) {
      fadeWavesOut();
    }
  }, [isOpen]);

  const isSevenStars = currentLevel === UserTierTypes.SEVEN_STARS;
  const currentTierCredits = tierCredits;
  const sevenStarsExperiencePercentage =
    getSevenStarsExperienceProgress(tierCredits);
  const sevenStarsMinCredits =
    allTiers?.find(
      tier => tier.guestprofiletiercode === UserTierTypes.SEVEN_STARS
    )?.mincredits ?? currentTierInfo?.mincredits;
  // Experience Credits only accrue once the guest is back above the Seven Stars
  // minimum, so a Seven Stars whose credits reset on 1/1 is earning the tier
  // back rather than working toward the next Experience Credit.
  const showExperienceCreditProgress =
    isSevenStars &&
    !!sevenStarsMinCredits &&
    tierCredits >= sevenStarsMinCredits;
  const earnedDates =
    lastEarnedDate && expirationDate ? (
      <View paddingTop="24px" alignItems="center">
        <View flexDirection={'row'}>
          <Text fontFamily="body" fontSize={'14px'} color={shuttleGray}>
            Last credits earned: <Text fontWeight={600}>{lastEarnedDate}</Text>
          </Text>
        </View>
        <Text
          paddingTop={'12px'}
          fontFamily="body"
          fontSize={'14px'}
          color={shuttleGray}
          textAlign="center">
          Earn more Reward Credits before{' '}
          <Text fontWeight={600} fontSize={'14px'}>
            {expirationDate}
          </Text>{' '}
          to retain your balance
        </Text>
      </View>
    ) : null;

  const {
    percentage,
    remainingCredits,
    targetName,
    byInvitation,
    hideLabel,
    invitationRequired,
  } = useMemo(
    () =>
      getTierProgress({
        tierCredits,
        currentLevel,
        currentTierInfo,
        nextTierInfo,
        allTiers,
      }),
    [tierCredits, currentLevel, currentTierInfo, nextTierInfo, allTiers]
  );

  // Render placeholder to maintain layout while tier data loads
  if (!currentLevel || !currentTierInfo) {
    return (
      <View
        height={isDesktop ? '525px' : undefined}
        width={isDesktop ? '343px' : '100%'}
        backgroundColor={white}
        shadow={0}
        padding={isDesktop ? '48px' : '24px'}
        mr={isDesktop ? '48px' : undefined}
        overflow="hidden"
      />
    );
  }

  const maxValueBeforeOverflow = 999999999;
  const rewardCreditsFormatted = rewardCredits
    ? rewardCredits > maxValueBeforeOverflow
      ? formatNumber(maxValueBeforeOverflow)
      : formatNumber(rewardCredits)
    : 0;

  const trackColor = tierColors[currentLevel]?.track || '#BEC0C5';

  const getContainerHeight = () => {
    if (!isSevenStars && rewardCredits < 0) {
      return '300px';
    }

    if (isSevenStars) {
      if (isLG) {
        if (lastEarnedDate && expirationDate) {
          return '810px';
        }
        return '740px';
      }
      return '690px';
    }

    if (isLG) {
      return '622px';
    }
    return isWeb ? '525px' : '500px';
  };

  return (
    <View
      height={isDesktop ? getContainerHeight() : undefined}
      width={isDesktop ? '343px' : '100%'}
      backgroundColor={white}
      shadow={0}
      padding={isDesktop ? '48px' : '24px'}
      mr={isDesktop ? '48px' : undefined}
      overflow="hidden">
      {isDesktop && (
        <Animated.View
          style={{
            transform: [{scaleX: -1}],
            position: 'absolute',
            bottom: 0,
            right: isWeb ? -18 : isDesktop ? -48 : -24,
            width: 300,
            opacity: fadeAnim,
          }}>
          <SVG source={BottomRightWaves} width={300} height={200} />
        </Animated.View>
      )}
      <View
        flexDirection={isDesktop ? 'column' : 'row'}
        justifyContent={isDesktop ? undefined : 'space-between'}>
        <UserInfoSummary
          fullName={fullName}
          accountId={accountId}
          currentLevel={currentLevel}
          toggleExpand={() => setIsOpen(!isOpen)}
          isOpen={isOpen}
          isGiftedTier={isGiftedTier}
        />
        {isDesktop || rewardCredits < 0 ? null : (
          <View alignItems="flex-end" justifyContent="flex-end">
            <Text
              fontFamily="heading"
              fontWeight="bold"
              fontSize="18px"
              paddingBottom="8px"
              numberOfLines={1}
              color={tuna}>
              {rewardCreditsFormatted}
            </Text>
            <Text
              fontFamily="body"
              fontWeight="bold"
              fontSize="9px"
              letterSpacing="1px"
              color={paleSky}
              textTransform="uppercase">
              reward credits
            </Text>
          </View>
        )}
      </View>

      {rewardCredits < 0 ? (
        <View paddingTop={isDesktop ? '48px' : '24px'} alignItems="center">
          <Text
            fontFamily="body"
            fontWeight="bold"
            fontSize="12px"
            letterSpacing="1px"
            color={paleSky}
            textTransform="uppercase">
            Error, credits not available
          </Text>
        </View>
      ) : isDesktop ? (
        <View>
          <Divider />
          <View paddingTop="8px">
            <View alignItems="center">
              <Text
                fontFamily="heading"
                fontWeight="bold"
                fontSize="40px"
                lineHeight="40px"
                paddingBottom="8px"
                color={shuttleGray}>
                {rewardCreditsFormatted}
              </Text>
              <Text
                fontFamily="body"
                fontWeight="bold"
                fontSize="9px"
                letterSpacing="1px"
                color={paleSky}
                textTransform="uppercase">
                reward credits
              </Text>
            </View>
            <View marginTop="24px" marginBottom="24px" alignItems="center">
              {isSevenStars && (
                <View borderRadius="8px" overflow="hidden">
                  <SVG source={SEVEN_STARS_PATTERN} height={388} width={248} />
                </View>
              )}
              <View
                position={isWeb && !isSevenStars ? 'relative' : 'absolute'}
                alignItems="center">
                <View
                  mb="24px"
                  alignItems="center"
                  pt={isSevenStars ? '32px' : undefined}
                  paddingX={isSevenStars ? '48px' : '76px'}>
                  <AnimatedCircularProgress
                    size={142}
                    width={16}
                    fill={
                      (isSevenStars
                        ? sevenStarsExperiencePercentage
                        : percentage) * 100
                    }
                    tintColor={trackColor}
                    onAnimationComplete={() => {}}
                    backgroundColor={
                      isSevenStars ? addAlpha(richBlack, 0.4) : athens
                    }
                    fillLineCap="round" //subject to change if they want no track at 0%
                    rotation={360}>
                    {() =>
                      isSevenStars ? (
                        <SevenStarsInnerCircle
                          tierCredits={currentTierCredits}
                        />
                      ) : (
                        <InnerCircle tierCredits={currentTierCredits} />
                      )
                    }
                  </AnimatedCircularProgress>
                </View>
                {showExperienceCreditProgress ? (
                  <View alignItems="center" paddingX="24px">
                    <Text
                      fontFamily="heading"
                      fontWeight="bold"
                      fontSize="24px"
                      lineHeight="28px"
                      color={white}>
                      {formatNumber(
                        getCreditsToNextExperienceCredit(tierCredits)
                      )}
                    </Text>
                    <Text
                      paddingTop="4px"
                      fontFamily="body"
                      fontSize="13px"
                      lineHeight="18px"
                      color={white}
                      textAlign="center">
                      Tier Credits to your next Seven Stars Experience Credit
                    </Text>
                    <View paddingTop="20px">
                      <SVG
                        source={SEVEN_STARS_TEXT_LOGO}
                        height={24}
                        width={97}
                      />
                    </View>
                  </View>
                ) : (
                  <NextTierCredits
                    currentLevel={currentLevel}
                    remainingCreditForNextLevel={remainingCredits}
                    nextTier={targetName}
                    isGiftedTier={isGiftedTier}
                    byInvitation={byInvitation}
                    hideLabel={hideLabel}
                    invitationRequired={invitationRequired}
                  />
                )}
                {isSevenStars ? null : earnedDates}
              </View>
              {/* The panel art is a fixed height the absolute overlay can't
              grow, so Seven Stars dates sit below the card instead of on it. */}
              {isSevenStars ? earnedDates : null}
            </View>
          </View>
        </View>
      ) : (
        <>
          <View paddingTop="12px">
            <>
              <ProgressBar
                color={trackColor}
                size={10}
                percentage={percentage}
              />

              <View
                justifyContent="space-between"
                flexDirection="row"
                marginTop="4px">
                <CurrentTierCredits tierCredits={currentTierCredits} />
                <NextTierCredits
                  currentLevel={currentLevel}
                  remainingCreditForNextLevel={remainingCredits}
                  nextTier={targetName}
                  isGiftedTier={isGiftedTier}
                  byInvitation={byInvitation}
                  hideLabel={hideLabel}
                  invitationRequired={invitationRequired}
                />
              </View>
            </>
          </View>
          {lastEarnedDate && expirationDate ? (
            <>
              <Text
                paddingTop={'8px'}
                fontFamily="body"
                fontSize={'11px'}
                color={shuttleGray}>
                Last credits earned:{' '}
                <Text fontWeight={600}>{lastEarnedDate}</Text>
              </Text>
              <Text
                paddingTop={'8px'}
                fontFamily="body"
                fontSize={'11px'}
                color={shuttleGray}>
                Earn more Reward Credits before{' '}
                <Text fontWeight={600}>{expirationDate}</Text> to retain your
                balance
              </Text>
            </>
          ) : null}
        </>
      )}
    </View>
  );
};

export default RewardsSummaryModule;
